import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../apiBase';

const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20
};
const card = {
    background: '#fff', borderRadius: 16, maxWidth: 540, width: '100%',
    padding: 28, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
};

const fmt = (n) => new Intl.NumberFormat().format(n ?? 0);

export default function ImportStudentsModal({ schoolId, api, onClose, onDone }) {
    const { t } = useTranslation(['importStudents', 'importHistory', 'common']);
    const [file, setFile] = useState(null);
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [estimate, setEstimate] = useState(null);
    const [error, setError] = useState('');

    async function downloadTemplate() {
        const base = getApiBaseUrl();
        const token = localStorage.getItem('token');
        const res = await axios.get(`${base}/schools/${schoolId}/import/template`, {
            responseType: 'blob',
            headers: { Authorization: `Bearer ${token}` }
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sportprogress_import_template.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
    }

    async function downloadErrorsFile() {
        if (!result?.importId) return;
        const base = getApiBaseUrl();
        const token = localStorage.getItem('token');
        const res = await axios.get(`${base}/schools/${schoolId}/import/errors-file`, {
            params: { importId: result.importId },
            responseType: 'blob',
            headers: { Authorization: `Bearer ${token}` }
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `import_errors_${schoolId}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    function onFilePick(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.name.toLowerCase().endsWith('.xlsx')) {
            setError(t('importStudents:xlsxOnly'));
            return;
        }
        if (f.size > 5 * 1024 * 1024) {
            setError(t('importStudents:maxSize'));
            return;
        }
        setError('');
        setFile(f);
        runEstimate(f);
    }

    async function runEstimate(f) {
        setLoading(true);
        setEstimate(null);
        try {
            const fd = new FormData();
            fd.append('file', f);
            const res = await api.post(`/schools/${schoolId}/import/estimate`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setEstimate(res.data);
            setStep(2);
        } catch (err) {
            setError(err.response?.data?.message || t('importStudents:estimateError'));
        } finally {
            setLoading(false);
        }
    }

    async function upload(confirmSmsCharge = false) {
        if (!file) return;
        setLoading(true);
        setError('');
        setStep(3);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const url = `/schools/${schoolId}/import${confirmSmsCharge ? '?confirmSmsCharge=true' : ''}`;
            const res = await api.post(url, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setResult(res.data);
            onDone?.();
        } catch (err) {
            const d = err.response?.data;
            if (err.response?.status === 402 && d?.estimate) {
                setEstimate(d.estimate);
                setStep(2);
                setError(t('importStudents:confirmSmsCharge'));
            } else {
                setError(d?.message || d?.estimate?.blockReason || t('importStudents:importError'));
                setStep(2);
            }
        } finally {
            setLoading(false);
        }
    }

    const problemRows = (result?.rows || []).filter(
        (r) => r.status === 'error' || r.status === 'skipped'
    );
    const previewRows = problemRows.slice(0, 10);
    const extraRows = Math.max(0, problemRows.length - previewRows.length);

    const needsConfirm = estimate?.smsServiceEnabled && !estimate?.preferEmailOnly
        && estimate?.phoneCount > 0 && estimate?.totalCharge > 0;

    return (
        <div style={overlay} onClick={onClose}>
            <div style={card} onClick={(e) => e.stopPropagation()}>
                <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>{t('importStudents:title')}</h2>

                {step === 1 && (
                    <>
                        <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>
                            {t('importStudents:intro')}
                        </p>
                        <p style={{ color: '#9a3412', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: '8px 12px', fontSize: 13, lineHeight: 1.5, margin: '0 0 12px' }}>
                            {t('importStudents:citizenshipTemplateHint')}
                        </p>
                        <button type="button" onClick={downloadTemplate} style={{
                            width: '100%', padding: 12, marginBottom: 16,
                            background: '#EB3333', color: '#fff', border: 'none',
                            borderRadius: 10, fontWeight: 600, cursor: 'pointer'
                        }}>
                            {t('importStudents:downloadTemplate')}
                        </button>
                        <label style={{
                            display: 'block', border: '2px dashed #cbd5e1', borderRadius: 12,
                            padding: 24, textAlign: 'center', cursor: 'pointer', color: '#64748b'
                        }}>
                            <input type="file" accept=".xlsx" style={{ display: 'none' }} onChange={onFilePick} />
                            {loading ? t('importStudents:estimating') : t('importStudents:pickFile')}
                        </label>
                        {error && <p style={{ color: '#c62828', fontSize: 13, marginTop: 8 }}>{error}</p>}
                    </>
                )}

                {step === 2 && estimate && (
                    <>
                        <p style={{ fontSize: 14 }}>
                            {t('importStudents:fileLabel', { name: file?.name })}
                        </p>
                        <div style={{
                            background: '#f8fafc', borderRadius: 10, padding: 14, marginTop: 12, fontSize: 13
                        }}>
                            <p>{t('importStudents:phonesInFile', { count: estimate.phoneCount })}</p>
                            <p>{t('importStudents:emailOnlyCount', { count: estimate.emailOnlyCount })}</p>
                            {estimate.preferEmailOnly && (
                                <p style={{ color: '#EB3333' }}>{t('importStudents:emailOnlyMode')}</p>
                            )}
                            {!estimate.smsServiceEnabled && estimate.phoneCount > 0 && (
                                <p style={{ color: '#e65100' }}>{t('importStudents:smsDisabled')}</p>
                            )}
                            {needsConfirm && (
                                <>
                                    <p style={{ marginTop: 8 }}>
                                        {t('importStudents:chargeTotal', { amount: fmt(estimate.totalCharge) })}
                                        {estimate.monthlyFeeIfDue > 0
                                            && t('importStudents:chargeWithFee', { fee: fmt(estimate.monthlyFeeIfDue) })}
                                    </p>
                                    <p>
                                        {t('importStudents:smsLine', {
                                            count: estimate.phoneCount,
                                            cost: fmt(estimate.smsCost)
                                        })}
                                    </p>
                                    <p>{t('importStudents:smsBalance', { balance: fmt(estimate.currentBalance) })}</p>
                                </>
                            )}
                            {estimate.alternativeHint && (
                                <p style={{ color: '#64748b', marginTop: 8 }}>{estimate.alternativeHint}</p>
                            )}
                            {estimate.blockReason && (
                                <p style={{ color: '#c62828', marginTop: 8 }}>{estimate.blockReason}</p>
                            )}
                        </div>
                        {error && <p style={{ color: '#c62828', fontSize: 13 }}>{error}</p>}
                        {estimate.canProceed !== false ? (
                            <button
                                type="button"
                                disabled={loading}
                                onClick={() => upload(needsConfirm)}
                                style={{
                                    width: '100%', padding: 12, marginTop: 12,
                                    background: '#1A1A1A', color: '#fff', border: 'none',
                                    borderRadius: 10, fontWeight: 600, cursor: 'pointer'
                                }}
                            >
                                {needsConfirm
                                    ? t('importStudents:importWithCharge', { amount: fmt(estimate.totalCharge) })
                                    : t('importStudents:importProceed')}
                            </button>
                        ) : (
                            <p style={{ marginTop: 12, fontSize: 13 }}>{t('importStudents:topUpHint')}</p>
                        )}
                        <button
                            type="button"
                            onClick={() => { setStep(1); setFile(null); setEstimate(null); }}
                            style={{ marginTop: 12, background: 'none', border: 'none', color: '#EB3333', cursor: 'pointer' }}
                        >
                            {t('importStudents:back')}
                        </button>
                    </>
                )}

                {step === 3 && loading && (
                    <p style={{ textAlign: 'center', padding: 24 }}>{t('importStudents:processing')}</p>
                )}

                {step === 3 && !loading && result && (
                    <div>
                        <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>{t('importStudents:importSuccess')}</h3>
                        <p style={{ margin: '6px 0' }}>{t('importStudents:importedCount', { count: result.imported ?? 0 })}</p>
                        {(result.linked ?? 0) > 0 && (
                            <p style={{ margin: '6px 0' }}>{t('importStudents:linkedCount', { count: result.linked })}</p>
                        )}
                        {(result.skipped ?? 0) + (result.errors ?? 0) > 0 && (
                            <p style={{ margin: '6px 0', color: '#b45309' }}>
                                {t('importStudents:errorsCount', {
                                    count: (result.skipped ?? 0) + (result.errors ?? 0)
                                })}
                            </p>
                        )}

                        {previewRows.length > 0 && (
                            <div style={{
                                marginTop: 16, padding: 12, background: '#f8fafc',
                                borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13
                            }}>
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                                    {t('importStudents:errorRows')}:
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ textAlign: 'left', color: '#64748b' }}>
                                            <th style={{ padding: '4px 6px' }}>{t('importStudents:colRow')}</th>
                                            <th style={{ padding: '4px 6px' }}>{t('importStudents:colName')}</th>
                                            <th style={{ padding: '4px 6px' }}>{t('importStudents:colPhone')}</th>
                                            <th style={{ padding: '4px 6px' }}>{t('importStudents:colReason')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((row, i) => (
                                            <tr key={`${row.rowNumber}-${i}`} style={{ borderTop: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '6px', verticalAlign: 'top' }}>{row.rowNumber}</td>
                                                <td style={{ padding: '6px', verticalAlign: 'top' }}>{row.name || '—'}</td>
                                                <td style={{ padding: '6px', verticalAlign: 'top' }}>{row.phone || '—'}</td>
                                                <td style={{ padding: '6px', verticalAlign: 'top', color: '#b45309' }}>
                                                    {row.note || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {extraRows > 0 && (
                                    <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: 12 }}>
                                        {t('importStudents:andMoreRows', { count: extraRows })}
                                    </p>
                                )}
                            </div>
                        )}

                        {result.hasErrors && (
                            <button
                                type="button"
                                onClick={downloadErrorsFile}
                                style={{
                                    width: '100%', padding: 12, marginTop: 16,
                                    background: '#fff', color: '#1A1A1A', border: '1px solid #e2e8f0',
                                    borderRadius: 10, fontWeight: 600, cursor: 'pointer'
                                }}
                            >
                                📥 {t('importStudents:downloadErrors')}
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                width: '100%', marginTop: 12, padding: '10px 16px',
                                borderRadius: 8, border: 'none', background: '#1A1A1A',
                                color: '#fff', cursor: 'pointer', fontWeight: 600
                            }}
                        >
                            {t('importStudents:closeBtn')}
                        </button>
                    </div>
                )}

                {step < 3 && (
                    <button
                        type="button"
                        onClick={onClose}
                        style={{ marginTop: 16, background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}
                    >
                        {t('common:cancel')}
                    </button>
                )}
            </div>
        </div>
    );
}
